import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  IContact,
  IMeeting,
  useAllMeetingsForContactQuery,
  useDeleteMeetingMutation
} from '../../../generated/graphql';
import OverviewTable, { IHeadCell } from '../../Common/Tables/OverviewTable';
import NewMeeting from '../NewMeeting';
import { Actions } from '../../../enums/ActionEnums';
import { AppContext } from '../../../AppContext';
import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '../../../utils/constants';
import { calculateMeetingDuration } from '../../../utils/mathUtils';

interface IProps {
  contact: IContact;
}

const ContactMeetingsTab: React.FunctionComponent<IProps> = ({ contact }) => {
  const { dispatch } = useContext(AppContext);
  const [allMeetings, setAllMeetings] = useState<IMeeting[]>([]);
  const [openContactMeetingForm, setOpenContactMeetingForm] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<IMeeting | null>(null);
  const [deleteMeeting] = useDeleteMeetingMutation();
  const { data, loading } = useAllMeetingsForContactQuery({
    variables: { contact_id: Number(contact.id) },
    fetchPolicy: 'no-cache'
  });

  const handleAddMeeting = (meeting: IMeeting) => {
    setAllMeetings([meeting, ...(allMeetings || [])]);
  };

  const handleEdit = (editedMeeting: IMeeting) => {
    setAllMeetings(allMeetings?.map((m) => (m?.id === editedMeeting.id ? editedMeeting : m)));
  };

  useEffect(() => {
    if (data && data.result) {
      const all = data.result as IMeeting[];
      setAllMeetings(all);
    }
  }, [data]);

  const handleDetails = (rowData: IMeeting) => {
    setSelectedMeeting(rowData);
    setOpenContactMeetingForm(true);
  };

  const handleRemove = (rowData: IMeeting) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IMeeting) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteMeeting({
      variables: {
        id: rowData.id
      },
      update(_, { data }) {
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: { text: `Meeting deleted ${rowData.name}`, severity: 'success' }
          });
          const filteredMeetings = allMeetings?.filter((m) => m?.id !== rowData.id);
          setAllMeetings(filteredMeetings);
        }
      }
    }).catch((error) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: error.message, severity: 'error', autoHide: 6000 }
      });
    });
  };

  const formatDate = (rowData: IMeeting, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
      : '';
  };

  const columns = [
    { title: 'Details', sorting: false, width: 51 },
    { title: 'Meeting Name', field: 'name', width: 51 },
    { title: 'Location', field: 'location', width: 51 },
    { title: 'Subject', field: 'subject', width: 51 },
    { title: 'Type', field: 'meeting_type.name', width: 51 },
    {
      title: 'Start Time',
      field: 'start_time',
      width: 51,
      type: 'date',
      render: (rowData: IMeeting) => formatDate(rowData, 'start_time')
    },
    {
      title: 'End Time',
      field: 'end_time',
      width: 51,
      type: 'date',
      render: (rowData: IMeeting) => formatDate(rowData, 'end_time')
    },
    {
      title: 'Duration',
      field: 'duration',
      width: 51,
      type: 'date',
      render: (rowData: IMeeting) =>
        calculateMeetingDuration(formatDate(rowData, 'start_time'), formatDate(rowData, 'end_time'))
    },
    { title: 'Remove', sorting: false, width: 70 }
  ];
  return (
    <Box className="overviewPage">
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns as IHeadCell[]}
          data={allMeetings || []}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={handleRemove}
          pageSize={15}
          createButtonProps={{
            id: 'create-contact-meeting-button',
            onClick: () => setOpenContactMeetingForm(true),
            text: 'Create Meeting'
          }}
        />
        {openContactMeetingForm && (
          <NewMeeting
            handleClose={() => {
              setOpenContactMeetingForm(false);
              setSelectedMeeting(null);
            }}
            meetingInput={selectedMeeting}
            contactId={Number(contact.id)}
            updateList={handleAddMeeting}
            editList={handleEdit}
          />
        )}
      </Box>
    </Box>
  );
};

export default ContactMeetingsTab;
